// Dark Futuristic Space Theme - Interactive Effects
// NASA-Inspired Mission Control Interface

class SpaceTheme {
  constructor() {
    this.initializeTheme();
    this.setupInteractiveEffects();
    this.startAmbientAnimations();
  }

  initializeTheme() {
    // Add theme class to body
    document.body.classList.add('space-theme');

    // Initialize particle systems
    this.initializeParticles();

    // Setup audio context for harmonic effects
    this.setupAudioContext();
  }

  setupInteractiveEffects() {
    // Hover effects for mission panels
    this.setupPanelEffects();

    // Navigation glow effects
    this.setupNavigationEffects();

    // Button interaction effects
    this.setupButtonEffects();

    // Form focus effects
    this.setupFormEffects();
  }

  initializeParticles() {
    // Create floating particles background
    const particleContainer = document.createElement('div');
    particleContainer.className = 'particle-field';
    particleContainer.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: -1;
      opacity: 0.6;
    `;

    // Generate floating particles
    for (let i = 0; i < 50; i++) {
      const particle = document.createElement('div');
      particle.className = 'particle';
      particle.style.cssText = `
        position: absolute;
        width: ${Math.random() * 3 + 1}px;
        height: ${Math.random() * 3 + 1}px;
        background: rgba(100, 181, 246, ${Math.random() * 0.5 + 0.3});
        border-radius: 50%;
        left: ${Math.random() * 100}%;
        top: ${Math.random() * 100}%;
        animation: float-particle ${Math.random() * 20 + 10}s infinite linear;
        box-shadow: 0 0 ${Math.random() * 10 + 5}px currentColor;
      `;
      particleContainer.appendChild(particle);
    }

    document.body.appendChild(particleContainer);
  }

  setupPanelEffects() {
    const panels = document.querySelectorAll('.mission-panel');

    panels.forEach(panel => {
      // Hover enter effect
      panel.addEventListener('mouseenter', (e) => {
        this.createEnergyPulse(e.target);
        this.playHoverSound();
      });

      // Mouse move effect
      panel.addEventListener('mousemove', (e) => {
        this.updatePanelGlow(e.target, e);
      });

      // Hover leave effect
      panel.addEventListener('mouseleave', (e) => {
        this.resetPanelGlow(e.target);
      });
    });
  }

  setupNavigationEffects() {
    const navLinks = document.querySelectorAll('.nav-link');

    navLinks.forEach(link => {
      link.addEventListener('mouseenter', () => {
        this.createNavBeam(link);
        this.playNavigationSound();
      });

      link.addEventListener('click', (e) => {
        this.createClickRipple(e.target, e);
        this.playActivationSound();
      });
    });
  }

  setupButtonEffects() {
    const buttons = document.querySelectorAll('.btn-primary, .btn-secondary');

    buttons.forEach(button => {
      button.addEventListener('mouseenter', () => {
        this.createButtonGlow(button);
      });

      button.addEventListener('mousedown', (e) => {
        this.createPressEffect(e.target, e);
        this.playButtonSound();
      });

      button.addEventListener('mouseup', (e) => {
        this.removePressEffect(e.target);
      });
    });
  }

  setupFormEffects() {
    const inputs = document.querySelectorAll('.form-input');

    inputs.forEach(input => {
      input.addEventListener('focus', (e) => {
        this.createInputActivation(e.target);
        this.playInputSound();
      });

      input.addEventListener('blur', (e) => {
        this.removeInputActivation(e.target);
      });

      input.addEventListener('input', (e) => {
        this.createTypingEffect(e.target);
      });
    });
  }

  createEnergyPulse(element) {
    const pulse = document.createElement('div');
    pulse.className = 'energy-pulse';
    pulse.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      width: 0;
      height: 0;
      border: 2px solid rgba(0, 230, 118, 0.6);
      border-radius: 50%;
      transform: translate(-50%, -50%);
      animation: energy-pulse 0.6s ease-out forwards;
      pointer-events: none;
      z-index: 10;
    `;

    element.style.position = 'relative';
    element.appendChild(pulse);

    // Remove after animation
    setTimeout(() => {
      if (pulse.parentNode) {
        pulse.parentNode.removeChild(pulse);
      }
    }, 600);
  }

  updatePanelGlow(panel, event) {
    const rect = panel.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    panel.style.background = `
      radial-gradient(circle at ${x}% ${y}%,
        rgba(100, 181, 246, 0.1) 0%,
        transparent 50%),
      linear-gradient(145deg,
        rgba(26, 26, 46, 0.9) 0%,
        rgba(22, 33, 62, 0.8) 100%)
    `;
  }

  resetPanelGlow(panel) {
    panel.style.background = `
      linear-gradient(145deg,
        rgba(26, 26, 46, 0.9) 0%,
        rgba(22, 33, 62, 0.8) 100%)
    `;
  }

  createNavBeam(link) {
    const beam = document.createElement('div');
    beam.className = 'nav-beam';
    beam.style.cssText = `
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 2px;
      background: linear-gradient(90deg,
        transparent,
        rgba(100, 181, 246, 0.8),
        transparent);
      animation: nav-beam-sweep 0.5s ease-out;
    `;

    link.style.position = 'relative';
    link.appendChild(beam);

    setTimeout(() => {
      if (beam.parentNode) {
        beam.parentNode.removeChild(beam);
      }
    }, 500);
  }

  createClickRipple(element, event) {
    const ripple = document.createElement('div');
    const rect = element.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = event.clientX - rect.left - size / 2;
    const y = event.clientY - rect.top - size / 2;

    ripple.style.cssText = `
      position: absolute;
      width: ${size}px;
      height: ${size}px;
      left: ${x}px;
      top: ${y}px;
      background: radial-gradient(circle,
        rgba(0, 230, 118, 0.4) 0%,
        transparent 70%);
      border-radius: 50%;
      transform: scale(0);
      animation: click-ripple 0.6s ease-out;
      pointer-events: none;
      z-index: 10;
    `;

    element.style.position = 'relative';
    element.appendChild(ripple);

    setTimeout(() => {
      if (ripple.parentNode) {
        ripple.parentNode.removeChild(ripple);
      }
    }, 600);
  }

  createButtonGlow(button) {
    button.style.transition = 'all 0.3s ease-in-out';
    button.style.boxShadow = `
      0 0 20px rgba(0, 230, 118, 0.6),
      0 0 40px rgba(0, 230, 118, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.3)
    `;
  }

  createPressEffect(button, event) {
    button.style.transform = 'translateY(1px) scale(0.98)';
    this.createClickRipple(button, event);
  }

  removePressEffect(button) {
    button.style.transform = 'translateY(-2px) scale(1)';
  }

  createInputActivation(input) {
    const activation = document.createElement('div');
    activation.className = 'input-activation';
    activation.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border: 2px solid rgba(0, 230, 118, 0.6);
      border-radius: inherit;
      animation: input-activation 0.3s ease-out;
      pointer-events: none;
      z-index: 10;
    `;

    input.style.position = 'relative';
    input.parentNode.appendChild(activation);

    setTimeout(() => {
      if (activation.parentNode) {
        activation.parentNode.removeChild(activation);
      }
    }, 300);
  }

  removeInputActivation(input) {
    input.style.borderColor = 'rgba(100, 181, 246, 0.3)';
  }

  createTypingEffect(input) {
    // Create brief glow on typing
    input.style.boxShadow = '0 0 10px rgba(0, 230, 118, 0.3)';

    setTimeout(() => {
      input.style.boxShadow = '0 0 5px rgba(100, 181, 246, 0.3)';
    }, 200);
  }

  setupAudioContext() {
    // Initialize Web Audio API for subtle sound effects
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.setupSoundEffects();
    } catch (e) {
      console.log('Web Audio API not supported');
    }
  }

  setupSoundEffects() {
    if (!this.audioContext) return;

    // Create different sound types
    this.sounds = {
      hover: { frequency: 800, duration: 0.1, volume: 0.02 },
      click: { frequency: 1200, duration: 0.15, volume: 0.03 },
      navigation: { frequency: 600, duration: 0.2, volume: 0.02 },
      input: { frequency: 1000, duration: 0.1, volume: 0.01 },
      activation: { frequency: 1500, duration: 0.3, volume: 0.03 }
    };
  }

  playSound(soundType) {
    if (!this.audioContext || !this.sounds[soundType]) return;

    const { frequency, duration, volume } = this.sounds[soundType];
    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    oscillator.frequency.setValueAtTime(frequency, this.audioContext.currentTime);
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(volume, this.audioContext.currentTime + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + duration);

    oscillator.start(this.audioContext.currentTime);
    oscillator.stop(this.audioContext.currentTime + duration);
  }

  playHoverSound() { this.playSound('hover'); }
  playNavigationSound() { this.playSound('navigation'); }
  playActivationSound() { this.playSound('activation'); }
  playButtonSound() { this.playSound('click'); }
  playInputSound() { this.playSound('input'); }

  startAmbientAnimations() {
    // Start subtle background animations
    this.animateBackground();
    this.animateParticles();
  }

  animateBackground() {
    // Subtle background color shift
    setInterval(() => {
      const hue = (Date.now() / 100) % 360;
      document.body.style.background = `
        linear-gradient(135deg,
          hsl(${hue}, 30%, 5%) 0%,
          hsl(${hue + 30}, 40%, 10%) 100%)
      `;
    }, 50);
  }

  animateParticles() {
    // Add CSS animations for particles
    const style = document.createElement('style');
    style.textContent = `
      @keyframes float-particle {
        0% {
          transform: translateY(100vh) rotate(0deg);
          opacity: 0;
        }
        10% {
          opacity: 1;
        }
        90% {
          opacity: 1;
        }
        100% {
          transform: translateY(-100px) rotate(360deg);
          opacity: 0;
        }
      }

      @keyframes energy-pulse {
        0% {
          width: 0;
          height: 0;
          opacity: 1;
        }
        100% {
          width: 100px;
          height: 100px;
          opacity: 0;
        }
      }

      @keyframes nav-beam-sweep {
        0% {
          transform: translateX(-100%);
          opacity: 0;
        }
        50% {
          opacity: 1;
        }
        100% {
          transform: translateX(100%);
          opacity: 0;
        }
      }

      @keyframes click-ripple {
        0% {
          transform: scale(0);
          opacity: 1;
        }
        100% {
          transform: scale(1);
          opacity: 0;
        }
      }

      @keyframes input-activation {
        0% {
          transform: scale(1);
          opacity: 0;
        }
        50% {
          transform: scale(1.02);
          opacity: 1;
        }
        100% {
          transform: scale(1);
          opacity: 0;
        }
      }
    `;
    document.head.appendChild(style);
  }
}

// Initialize theme when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  window.spaceTheme = new SpaceTheme();
});

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = SpaceTheme;
}
